/**
 * Warm→cold read line (phase 2, line E §5.8).
 *
 * Platform classifies a day from cold facts only and sends it on one of the
 * two cold-fill headers. The gateway, in the same snapshot as the usage read,
 * excludes `native` days unconditionally and `h0` days only when that day has
 * no hot row and the warm day is sealed. An absent header is not a read line:
 * the gateway runs no read-line SQL.
 */

export const HISTORY_READ_COLD_LINE_DAYS_ENV = 'HISTORY_READ_COLD_LINE_DAYS';
/**
 * Cold-fill only. Days whose cold revision is a full native row. The gateway
 * excludes them unconditionally. Absent header: the gateway runs no read-line
 * SQL and adds no read-line field.
 */
export const HISTORY_READ_COLD_LINE_NATIVE_HEADER = 'x-history-read-cold-line-native';
/**
 * Cold-fill only. Days whose cold revision is the warm-table copy (H0). The
 * gateway excludes a day only when that snapshot has no `usage_logs` row and
 * the warm day is sealed.
 */
export const HISTORY_READ_COLD_LINE_H0_HEADER = 'x-history-read-cold-line-h0';
/**
 * Cold-fill only, a subset of the native / h0 days on the same request. Days
 * whose current cold revision holds no `usage.logs` row (and, from the rated
 * start day, no `usage.rated_events` row): the cold layer proves them empty.
 * The aggregate readers answer such a day as empty instead of refusing it when
 * no day rollup was ever folded for it (production 2026-04-20..22: no traffic,
 * so neither the fold nor the warm archive left a mark). A day outside the
 * line headers is ignored.
 */
export const HISTORY_READ_COLD_LINE_EMPTY_HEADER = 'x-history-read-cold-line-empty';
/**
 * Response-only window echo for the internal usage routes. Pair keeps the
 * tenant-visible baseline byte-identical, so the candidate reads this instead
 * of a body field the off path does not carry.
 */
export const HISTORY_READ_WINDOW_FROM_HEADER = 'x-ndt-window-from';
export const HISTORY_READ_WINDOW_TO_HEADER = 'x-ndt-window-to';
/** Admin-only month anchor: the baseline's `monthlyWindow.to`. */
export const HISTORY_READ_WINDOW_MONTH_TO_HEADER = 'x-ndt-window-month-to';
/** @deprecated Use the native / h0 headers. Kept so older call sites still parse a day list. */
export const HISTORY_READ_COLD_LINE_DAYS_HEADER = HISTORY_READ_COLD_LINE_NATIVE_HEADER;

export type HistoryReadColdLineTier = 'hot' | 'warm';

/** One read-line day the gateway actually excluded, and the layer that would have served it. */
export interface HistoryReadColdLineServedDay {
  readonly day: string;
  readonly tier: HistoryReadColdLineTier;
}
/**
 * One day is the shortest line (10-21: the hot window may shrink to 3 days, and
 * to 1). A line shorter than `LIFECYCLE_METRICS_RETENTION_DAYS` (10) overlaps
 * lifecycle rows, which the cold tier does not carry: the tenant's elapsed
 * comes from the usage row's own `fb_ms` on those days, and the ops listings
 * join the gateway's lifecycle rows back (`/internal/usage/ops/lifecycle`).
 */
export const HISTORY_READ_COLD_LINE_MIN_DAYS = 1;
export const HISTORY_READ_COLD_LINE_MAX_DAYS = 400;

/**
 * The widest window the tenant time-series readers (`/v1/usage/hourly`,
 * `/hourly-units`, `/daily`) answer under `HISTORY_READ_ROUTING=on`, in days of
 * 24 hours. A wider request is a 400 naming this number, never a silently
 * shortened answer. A `days=400` rolling window touches 401 Shanghai calendar
 * days (both ends partial); the gateway-side day guards allow that one extra
 * day ({@link USAGE_WIDE_WINDOW_MAX_SHANGHAI_DAYS}).
 */
export const USAGE_WIDE_WINDOW_MAX_DAYS = 400;

/** Shanghai calendar days a {@link USAGE_WIDE_WINDOW_MAX_DAYS} window can touch: its two partial end days included. */
export const USAGE_WIDE_WINDOW_MAX_SHANGHAI_DAYS = USAGE_WIDE_WINDOW_MAX_DAYS + 1;

/**
 * The 16 hot-only scalar field ids an H0 warm row never had.
 * Source of the list: `rehearsal/cold-sim/datagen/lib/history.ts`
 * `H0_MISSING_SCALARS` (cold-arch-v3 §11.4), frozen 2026-10-18.
 * A legal manifest is `coverage: 'partial'` with gaps ⊆ this set:
 * cold-format requires `(coverage === 'complete') === (gaps.length === 0)`,
 * so "complete and these gaps" is not a publishable shape.
 */
export const USAGE_LOGS_H0_RETIRED_FIELD_IDS: readonly number[] = Object.freeze([
  3, 20, 27, 28, 29, 34, 40, 41, 44, 45, 46, 50, 51, 52, 53, 54,
]);

const H0_RETIRED_FIELD_ID_SET: ReadonlySet<number> = new Set(USAGE_LOGS_H0_RETIRED_FIELD_IDS);

/** True when every id is one of the 16 retired H0 scalars (an empty set is a subset). */
export function usageLogsH0RetiredGapsOnly(ids: readonly number[]): boolean {
  for (const id of ids) {
    if (!H0_RETIRED_FIELD_ID_SET.has(id)) return false;
  }
  return true;
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/u;

function realDay(day: string): boolean {
  const match = YMD.exec(day);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);
  const utc = new Date(Date.UTC(year, month - 1, date));
  return utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === date;
}

/**
 * `HISTORY_READ_COLD_LINE_DAYS`. Unset or blank is off. Anything that is not
 * an integer in `[MIN_DAYS, MAX_DAYS]` (1..400) is off plus a warning the caller logs once.
 */
export function parseHistoryReadColdLineDays(raw: string | undefined): { readonly days: number | null; readonly warning?: string } {
  const text = raw?.trim() ?? '';
  if (text === '') return { days: null };
  if (!/^[0-9]+$/u.test(text)) {
    return { days: null, warning: `${HISTORY_READ_COLD_LINE_DAYS_ENV} must be an integer from ${HISTORY_READ_COLD_LINE_MIN_DAYS} to ${HISTORY_READ_COLD_LINE_MAX_DAYS}` };
  }
  const days = Number(text);
  if (days < HISTORY_READ_COLD_LINE_MIN_DAYS || days > HISTORY_READ_COLD_LINE_MAX_DAYS) {
    return { days: null, warning: `${HISTORY_READ_COLD_LINE_DAYS_ENV} must be an integer from ${HISTORY_READ_COLD_LINE_MIN_DAYS} to ${HISTORY_READ_COLD_LINE_MAX_DAYS}` };
  }
  return { days };
}

/**
 * Cold-fill header: comma-separated `YYYY-MM-DD`, deduped and sorted.
 * A blank header is "no days". Any malformed token, or more than 400 days, is
 * a 400 — the gateway must not guess.
 */
export function parseHistoryReadColdLineDaysHeader(
  raw: string | undefined,
  header: string = HISTORY_READ_COLD_LINE_NATIVE_HEADER,
): { readonly ok: true; readonly days: readonly string[] } | { readonly ok: false; readonly error: string } {
  if (raw === undefined || raw.trim() === '') return { ok: true, days: [] };
  const days: string[] = [];
  for (const token of raw.split(',')) {
    const day = token.trim();
    if (!realDay(day)) {
      return { ok: false, error: `${header} must be comma-separated YYYY-MM-DD days` };
    }
    days.push(day);
  }
  const unique = [...new Set(days)].sort();
  if (unique.length > HISTORY_READ_COLD_LINE_MAX_DAYS) {
    return { ok: false, error: `${header} lists more than ${HISTORY_READ_COLD_LINE_MAX_DAYS} days` };
  }
  return { ok: true, days: unique };
}
